import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Info,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ApiRequestError } from '../api';

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger' | 'danger-solid';

export function Button({
  variant = 'default',
  size,
  icon: Icon,
  loading,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'lg';
  icon?: LucideIcon;
  loading?: boolean;
}) {
  const classes = [
    'btn',
    variant !== 'default' ? `btn-${variant}` : '',
    size ? `btn-${size}` : '',
    !children ? 'btn-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classes} disabled={loading || rest.disabled} {...rest}>
      {loading ? (
        <span className="spinner" style={{ width: 14, height: 14 }} />
      ) : Icon ? (
        <Icon aria-hidden />
      ) : null}
      {children}
    </button>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p className="desc">{description}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </header>
  );
}

export function Card({
  title,
  sub,
  actions,
  children,
  footer,
  flush,
  className = '',
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** No body padding (lists, tables). */
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {title ? (
        <div className="card-header">
          <div>
            <h2>{title}</h2>
            {sub ? <div className="sub">{sub}</div> : null}
          </div>
          {actions}
        </div>
      ) : null}
      {flush ? children : <div className="card-body">{children}</div>}
      {footer ? <div className="card-footer">{footer}</div> : null}
    </section>
  );
}

export function Stat({
  label,
  value,
  icon: Icon,
  foot,
  onClick,
  tone = 'accent',
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  foot?: ReactNode;
  onClick?: () => void;
  tone?: 'accent' | 'ok' | 'warn' | 'danger';
}) {
  const body = (
    <>
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon icon-tile ${tone}`} style={{ width: 32, height: 32 }}>
          <Icon aria-hidden />
        </span>
      </div>
      <div className="stat-value">{value}</div>
      {foot ? <div className="stat-foot">{foot}</div> : null}
    </>
  );
  return onClick ? (
    <button type="button" className="card stat" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="card stat">{body}</div>
  );
}

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'neutral';

export function Badge({
  tone = 'neutral',
  dot,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={`badge ${tone !== 'neutral' ? `badge-${tone}` : ''} ${dot ? 'badge-dot' : ''}`}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  active: 'ok',
  published: 'ok',
  inactive: 'neutral',
  disabled: 'danger',
  revoked: 'danger',
  pending: 'warn',
  locked: 'danger',
  dead: 'danger',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export function Field({
  label,
  error,
  hint,
  children,
  className = '',
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field ${className}`}>
      <span className="field-label">{label}</span>
      {children}
      {error ? (
        <span className="field-error">{error}</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </label>
  );
}

const CALLOUT_ICON = { info: Info, ok: CheckCircle2, warn: AlertTriangle, danger: AlertCircle };

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: keyof typeof CALLOUT_ICON;
  title?: ReactNode;
  children?: ReactNode;
}) {
  const Icon = CALLOUT_ICON[tone];
  return (
    <div className={`callout callout-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon aria-hidden />
      <div className="stack-sm" style={{ gap: 4 }}>
        {title ? <strong>{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  );
}

export function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  const details =
    error instanceof ApiRequestError && Array.isArray(error.details)
      ? (error.details as { path: string; message: string }[])
      : [];
  return (
    <Callout tone="danger" title={errorText(error)}>
      {details.length ? (
        <ul>
          {details.map((d) => (
            <li key={`${d.path}:${d.message}`}>
              {d.path ? <strong>{d.path}: </strong> : null}
              {d.message}
            </li>
          ))}
        </ul>
      ) : null}
    </Callout>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  children,
  action,
}: {
  icon: LucideIcon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon aria-hidden />
      </div>
      <h3>{title}</h3>
      {children ? <p style={{ maxWidth: 420 }}>{children}</p> : null}
      {action}
    </div>
  );
}

/** A secret shown exactly once (one-time password, PIN, enrollment code). */
export function OneTimeSecret({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Callout tone="ok" title={label}>
      <div className="row" style={{ marginTop: 6 }}>
        <span className="secret">{value}</span>
        <Button
          size="sm"
          icon={Copy}
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="small muted" style={{ marginTop: 6 }}>
        {note ?? 'Shown only once. Hand it over securely; it cannot be retrieved later.'}
      </p>
    </Callout>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v ?? <span className="faint">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Skeleton({
  width = '100%',
  height = 14,
}: {
  width?: number | string;
  height?: number;
}) {
  return <div className="skeleton" style={{ width, height }} />;
}

export function Avatar({ name, size }: { name: string; size?: 'lg' }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return <span className={`avatar ${size === 'lg' ? 'avatar-lg' : ''}`}>{initials || '?'}</span>;
}

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
}: {
  value: T;
  onChange: (v: T) => void;
  tabs: { id: T; label: string; count?: number }[];
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          className={`tab ${value === t.id ? 'active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count !== undefined ? <span className="count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
